import React from 'react';
import {
  ThemeProvider,
  createTheme,
  ThemeOptions,
  Theme,
  StyledEngineProvider
} from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { EdwardColors } from 'enums/colors';

const defaultTheme = createTheme();

export const borderRadiusButton = '18px';
export const borderRadiusBox = '6px';

// key theme customizations
export const appThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: EdwardColors.DARK,
    },
    secondary: {
      main: EdwardColors.SECONDARY,
    },
    text: {
      primary: '#000',
      secondary: '#000',
    },
    black: {
      main: '#000',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 1024,
      lg: 1280,
      xl: 1920,
    },
  },
  shape: {
    borderRadius: 6,
  },

  // overrides of default props
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
          letterSpacing: '0.01071em',
          color: 'rgba(0,0,0,0.87)'
        },
      },
    },

    MuiButton: {
      defaultProps: {
        variant: 'outlined',
        color: 'black',
        disableElevation: true,
        disableRipple: true,
        disableTouchRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        contained: {
          lineHeight: '1.7',
          paddingTop: '7px',
          borderRadius: borderRadiusButton,
          '&.corners': {
            borderRadius: 0,
          },
          '&.MuiButton-colorBlack': {
            color: '#fff',
            backgroundColor: '#000',
            '&:hover': {
              backgroundColor: EdwardColors.GRAY_MEDIUM,
            },
            '&:active': {
              backgroundColor: EdwardColors.GRAY_LIGHTER,
            },
            '&:disabled': {
              backgroundColor: EdwardColors.GRAY_LIGHT,
            },
          },
          '&.MuiButton-colorPrimary': {
            '&:hover': {
              backgroundColor: EdwardColors.MEDIUM,
              color: '#000',
            },
            '&:active': {
              color: '#fff',
              backgroundColor: EdwardColors.SECONDARY,
            },
            '&:disabled': {
              backgroundColor: EdwardColors.GRAY_LIGHT,
            },
          },
          '&.MuiButton-colorSecondary': {
            backgroundColor: EdwardColors.GRAY_MEDIUM,
            color: '#fff',
            '&:hover': {
              backgroundColor: EdwardColors.GRAY_LIGHTER,
            },
            '&:active': {
              color: '#fff',
              backgroundColor: EdwardColors.GRAY_LIGHT,
            },
            '&.secondaryGreen': {
              backgroundColor: EdwardColors.GREEN,
              '&:hover': {
                backgroundColor: EdwardColors.GREEN_LIGHTER,
              },
              '&:active': {
                color: '#fff',
                backgroundColor: '#000',
              },
              '&:disabled': {
                backgroundColor: EdwardColors.GRAY_LIGHT,
              },
            },
            '&.secondaryGold': {
              backgroundColor: EdwardColors.SECONDARY,
              '&:hover': {
                backgroundColor: EdwardColors.DARK,
              },
              '&:active': {
                color: '#fff',
                backgroundColor: '#000',
              },
              '&:disabled': {
                backgroundColor: EdwardColors.GRAY_LIGHT,
              },
            },
          }

        },
        outlined: {
          lineHeight: '1.7',
          paddingTop: '6px',
          borderRadius: borderRadiusButton,
          borderWidth: '2px',
          '&.corners': {
            borderRadius: 0,
          },
          borderColor: EdwardColors.DARK,
          '& svg': {
            color: EdwardColors.DARK,
          },
          '&.Mui-disabled svg': {
            color: 'rgba(0, 0, 0, 0.26)'
          },
          '&:hover': {
            lineHeight: '1.7',
            paddingTop: '6px',
            backgroundColor: EdwardColors.DARK,
            color: '#fff',
            '& svg': {
              color: '#fff',
            },
          },
          '&:active': {
            lineHeight: '1.7',
            paddingTop: '6px',
            color: '#fff',
            backgroundColor: EdwardColors.SECONDARY,
            borderColor: EdwardColors.SECONDARY,
          },
          '& .MuiButton-label': {
            order: 1,
          },
          '&.MuiButton-colorBlack': {
            '&:hover': {
              borderWidth: '2px',
              borderColor: EdwardColors.DARK,
            }
          },
          '&.MuiButton-colorPrimary': {
            lineHeight: '1.7',
            paddingTop: '6px',
            borderColor: EdwardColors.DARK,
            borderWidth: '2px',
            '&:hover': {
              lineHeight: '1.7',
              paddingTop: '6px',
              borderWidth: '2px',
              borderColor: EdwardColors.DARK,
              backgroundColor: EdwardColors.DARK,
              color: '#000',
              '& svg': {
                color: '#000',
              },
            },
            '&:active': {
              borderColor: EdwardColors.SECONDARY,
              backgroundColor: EdwardColors.SECONDARY,
            },
          },
          '&.MuiButton-colorSecondary': {
            lineHeight: '1.7',
            paddingTop: '4px',
            borderWidth: '2px',
            borderColor: '#000',
            color: '#000',
            '&:hover': {
              paddingTop: '4px',
              borderWidth: '2px',
              borderColor: '#000',
              color: '#000',
              backgroundColor: EdwardColors.GRAY_EXTRALIGHT,
              '& svg': {
                color: '#000',
              },
            },
            '& svg': {
              color: '#000',
            },
          }
        },
      }
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary'
      }
    },
    MuiContainer: {
      defaultProps: {
        disableGutters: true
      },
      /* doesnt work anymore:
      styleOverrides: {
        maxWidthMd: {
        padding: 0,
      }},
      */
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'lg',
        fullWidth: true,

      },
      styleOverrides: {
        paperScrollPaper: {
          maxHeight: `calc(100% - ${defaultTheme.spacing(10)})`,
          marginBottom: defaultTheme.spacing(7),
          marginTop: defaultTheme.spacing(3),
        },
        paperFullWidth: {
          [defaultTheme.breakpoints.down('sm')]: {
            width: `calc(100% - ${defaultTheme.spacing(2)})`,
            margin: defaultTheme.spacing(2),
          },
        }
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          '& + .MuiDialogContent-root': {
            paddingTop: `${defaultTheme.spacing(4)} !important`
          }
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          justifyContent: 'center',
          padding: defaultTheme.spacing(2, 1)
        }
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: defaultTheme.spacing(4),
          marginTop: defaultTheme.spacing(-1),
          [defaultTheme.breakpoints.down('sm')]: {
            padding: defaultTheme.spacing(2),
          },
        }
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          marginBottom: defaultTheme.spacing(2),
        }
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& fieldset': { borderWidth: 2 },
        }
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: defaultTheme.palette.text.primary,
        }
      },
    },
    MuiGrid: {
      defaultProps: {
        alignItems: 'flex-start',
        spacing: 2,
      },
      styleOverrides: {
        'spacing-xs-1': {
          width: 'calc(100% + 8px)',
          margin: '-4px',
          '& > .MuiGrid-item': {
            padding: '4px',
          },
        },
        'spacing-xs-2': {
          width: 'calc(100% + 16px)',
          margin: '-8px',
          '& > .MuiGrid-item': {
            padding: '8px',
          },
        },
        'spacing-xs-4': {
          width: 'calc(100% + 32px)',
          margin: '-16px',
          '& > .MuiGrid-item': {
            padding: '16px',
          },
        },
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: defaultTheme.spacing(1.5),
          //color: 'inherit'
          color: 'rgba(0,0,0,0.54)'
        },
        sizeSmall: {
          padding: '3px',


        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // original mui color for palette.text.secondary
          color: 'rgba(0, 0, 0, 0.54)',
        },
        shrink: {
          color: '#000',
        }
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: EdwardColors.SECONDARY,
        }
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: '6px',
          paddingBottom: '6px',
          '& .MuiListItemText-root': {
            marginTop: '4px',
            marginBottom: '4px'
          }
        }
      }
    },
    MuiRadio: {
      defaultProps: {
        color: 'secondary'
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: { minHeight: '1.1876em' }
      }
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          alignItems: 'center',
        },
        labelContainer: {
          alignItems: 'center',
          '& .MuiTypography-body2': {
            marginBottom: 0,
          },
        }
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: 'black',
          '&.Mui-active': {
            color: EdwardColors.SECONDARY,
          },
        },
        text: {
          fill: 'white',
        }
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        autoComplete: 'off',
        size: 'small',
      },
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderRadius: '4px',
          '& fieldset': { borderWidth: 2, borderRadius: '4px' },
        }
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          ...defaultTheme.typography.h4,
          fontSize: '28px',
          fontWeight: 800,
          marginBottom: defaultTheme.spacing(2),
        },
        h2: {
          ...defaultTheme.typography.h5,
          fontSize: '20px',
          fontWeight: 800,
          marginBottom: defaultTheme.spacing(1.2),
        },
        h3: {
          ...defaultTheme.typography.h6,
          fontSize: '18px',
          fontWeight: 800,
          marginBottom: defaultTheme.spacing(1),
        },
        h4: {
          fontSize: '14px',
          fontWeight: 800,
          marginBottom: defaultTheme.spacing(0.5),
        },
        h5: {
          fontSize: '12px',
          fontWeight: 800,
          marginBottom: defaultTheme.spacing(0.5),
        },
        body2: {
          lineHeight: 'normal',
          letterSpacing: 0,
          fontSize: 14,
          marginBottom: defaultTheme.spacing(1),
        },
      },
    },
  },
};

// theme generated from the theme customisations above
// so the overrides can acces theme values
export const appTheme: Theme = createTheme(appThemeOptions);

export const AppTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <StyledEngineProvider>
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  </StyledEngineProvider>
);
